@use '~/vendor/cxy/scss/styles/index.scss' as cxy;

.dropdown {
  position: relative;

  [data-testid='dropdown-label'],
  .option {
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
  }

  &,
  .label {
    cursor: pointer;
  }

  &.disabled {
    &,
    .trigger,
    .label {
      pointer-events: none;
      cursor: not-allowed;
    }
  }

  .label {
    margin-block-end: var(--cxy-spacing-3);
    display: inline-block;

    &.hiddenLabel {
      visibility: hidden;
      position: absolute;
    }
  }

  .trigger {
    background-color: transparent;
    width: 100%;
    text-decoration: none;
    margin: 0;
    padding: var(--cxy-spacing-xxsm);
    border-radius: var(--cxy-border-radius-s);
    border: var(--cxy-border-default);
    align-items: center;
    justify-content: space-between;
    width: 100%;
    display: flex;
    text-decoration: none;

    span {
      text-decoration: none;
    }
  }

  .list {
    list-style: none;
    margin: 0;
    padding: 0;
  }

  .trigger,
  .trigger[data-placeholder],
  .option {
    color: var(--cxy-color-text-default-body);

    @include cxy.font-size('text-s', var(--cxy-fontWeight-semiBold));

    @include cxy.screen-size('desktop') {
      @include cxy.font-size('text-m');
    }
  }

  .icon {
    display: flex;
    align-items: center;

    svg {
      transition: transform 0.15s ease;
    }

    &[data-open='true'] svg {
      transform: rotate(180deg);
    }
  }

  // Hidden unless a theme opts in, so themes that mark selection by other means are unchanged.
  .selectedIcon {
    display: none;
  }

  @media (prefers-reduced-motion: reduce) {
    .icon svg {
      transition: none;
    }
  }

  .popover {
    display: none;
  }

  .popover.isOpen {
    display: block;
    position: absolute;
    width: 100%;
    z-index: 10;
    margin-block-start: var(--cxy-spacing-xxxsm);
    border-radius: var(--cxy-border-radius-s);
    background-color: var(--cxy-color-surface-primary-default-subtle);
    border: var(--cxy-border-default);
    box-sizing: border-box;
    box-shadow: var(--cxy-box-shadow-a);
    height: var(--max-list-height, auto);
    overflow-y: auto;

    .option {
      padding: var(--cxy-spacing-xxsm);

      &[aria-selected='true'],
      &[data-highlighted='true'] {
        background-color: var(--cxy-color-surface-success-default-subtle-hover);
      }
    }
  }
}

.size-s {
  .label {
    margin-block-end: var(--cxy-spacing-xxxsm);
  }
}

/*
 * Collapsed trigger is NOT in the Figma design; it is derived from the most-popular-bets sport
 * filter, the only place it was defined. Confirm with design before treating it as settled.
 *
 * Every value reads `var(--cxy-dropdown-*, <token>)` so a host can retheme this control on its own
 * by setting the variable on an ancestor — declaring the defaults on the root instead would beat
 * the inherited override.
 */
[data-fpsm-theme='oddschecker'] {
  .dropdown {
    .trigger {
      background-color: var(--cxy-dropdown-surface, var(--cxy-color-surface-default));
      border: var(--cxy-borderWidth-default) var(--cxy-dropdown-trigger-border, var(--cxy-color-content-feature));
      color: var(--cxy-dropdown-text, var(--cxy-color-content-feature));
      gap: var(--cxy-spacing-xxsm);

      // Button's own oddschecker block uppercases every `.button`; a sport name is a label, not a
      // call to action, and the filter this replaces rendered it as written.
      text-transform: none;

      // 32px by construction (8px padding + 14px line-height + 1px border). A host whose control is
      // a touch target can raise it; `min-height` rather than `height` so the content still fits.
      min-height: var(--cxy-dropdown-trigger-min-height, auto);
    }

    /*
     * `IconChevronDown` defaults to `color: white` at `size: sm`, so on the white trigger it is an
     * invisible 20px glyph. The filter this replaces used a 12px chevron in the text colour.
     */
    .icon svg {
      color: var(--cxy-dropdown-text, var(--cxy-color-content-feature));
      height: var(--cxy-icon-size-xxsm);
      width: var(--cxy-icon-size-xxsm);
    }

    /*
     * The label span needs the size stated on it, not just inherited from the trigger: with only
     * an inherited value the browser renders it at the root size. The filter this replaces set it
     * on its own label element for the same reason.
     */
    .trigger,
    .trigger [data-testid='dropdown-label'],
    .option {
      @include cxy.font-size('button-xxs');
    }

    // 14px, not `button-xxs`'s 16px: with 8px padding and a 1px border this is what makes the
    // collapsed control 32px tall, matching the filter it replaces. Must stay AFTER the
    // `font-size` include above, which sets its own line-height.
    .trigger,
    .trigger [data-testid='dropdown-label'] {
      line-height: var(--cxy-lineHeight-xxxs);
    }

    .option {
      font-weight: var(--cxy-fontWeight-normal);
    }

    // Whole-control only: the design shows a per-row disabled state, which this component
    // does not model (options carry no disabled flag).
    &.disabled .trigger {
      color: var(--cxy-dropdown-text-disabled, var(--cxy-color-content-muted));
    }

    .popover.isOpen {
      background-color: var(--cxy-dropdown-surface, var(--cxy-color-surface-default));
      border: var(--cxy-borderWidth-default) var(--cxy-dropdown-border, var(--cxy-color-border-subtle));
      /*
       * Standard properties only. Chromium 121+ ignores the ::-webkit-scrollbar-* pseudo-elements
       * once either of these is set, and Firefox never supported them, so a webkit block here would
       * be dead code that reads as load-bearing. The cost is that the design's 3px thumb is not
       * expressible — `thin` is the narrowest the standard allows, and it is what the sport filter
       * has always actually rendered. Raised with design rather than faked.
       */
      scrollbar-color: var(--cxy-dropdown-scrollbar-thumb, var(--cxy-color-text-on-color-caption)) transparent;
      scrollbar-width: thin;

      /*
       * Wider than the trigger, and right-aligned so it grows leftward. The filter this replaces
       * used reakit's `placement: 'bottom-end'` for the same reason: left-aligned, a 156px menu on
       * a content-sized trigger runs past the viewport edge, taking the selected row's check with it.
       */
      min-width: var(--cxy-dropdown-menu-min-width, auto);
      /*
       * Overridable because the edge that is safe depends on where the trigger sits: a menu wider
       * than its trigger runs off whichever side it grows towards, and a host with the trigger near
       * the left edge of a clipping container needs the opposite pair.
       */
      inset-inline-start: var(--cxy-dropdown-menu-inset-start, auto);
      inset-inline-end: var(--cxy-dropdown-menu-inset-end, 0);

      .option {
        color: var(--cxy-dropdown-text, var(--cxy-color-content-feature));

        /*
         * Separator drawn as an inset shadow, NOT a border: `Dropdown.tsx` sizes the scroll
         * viewport as `itemHeight * maxItemsVisible` with `itemHeight = 32`, so a border would
         * make rows 33px and clip the last visible one. The design specifies 32px rows.
         */
        &:not(:last-child) {
          box-shadow: inset 0 -1px 0 0 var(--cxy-dropdown-border, var(--cxy-color-border-subtle));
        }

        /*
         * The base rule fills the selected row with `container-light`; the design does not — it
         * marks selection with the check alone, so that fill is cleared here. `position` and the
         * reserved padding are for the absolutely-positioned check, which keeps the row at 32px
         * and the label's ellipsis intact.
         */
        &[aria-selected='true'] {
          background-color: transparent;
          position: relative;
          padding-inline-end: calc(var(--cxy-spacing-xsm) + var(--cxy-icon-size-xxsm));
        }

        /*
         * The fill means "the pointer is here". The `:not()` matters because downshift highlights
         * the selected row when the menu opens — without it that row opens filled with nothing
         * hovering it. Must stay AFTER the reset above.
         */
        &[data-highlighted='true']:not([aria-selected='true']),
        &[aria-selected='true']:hover {
          background-color: var(--cxy-dropdown-row-highlight, var(--cxy-color-surface-page-secondary));
        }
      }

      .selectedIcon {
        display: flex;
        align-items: center;
        position: absolute;
        inset-block-start: 50%;
        inset-inline-end: var(--cxy-spacing-xxsm);
        transform: translateY(-50%);

        /*
         * 16px is the design's frame, which puts the glyph's circle at ~13px as drawn. It equals
         * `--cxy-icon-size-xsm`; moving hand-typed icon widths onto the token is a follow-up.
         */
        svg {
          color: var(--cxy-dropdown-text, var(--cxy-color-content-feature));
          height: var(--cxy-dropdown-check-size, 16px);
          width: var(--cxy-dropdown-check-size, 16px);
        }
      }
    }
  }
}

[data-fpsm-theme='gazzetta'] {
  .icon svg {
    color: var(--cxy-color-icon-black) !important;
  }

  .popover.isOpen {
    background-color: var(--cxy-color-surface-success-default-subtle-hover);

    .option {
      &[aria-selected='true'],
      &[data-highlighted='true'] {
        background-color: var(--cxy-color-surface-primary-default-subtle);
      }
    }
  }
}
